/**
 * AgentKeyAppAccess — which apps (plugins) one agent API key may use
 * (ADR-025 §4).
 *
 * An app chooses which of its data it shares with agents; the user then lets
 * a specific key use a specific app, collection by collection, read or read
 * and change. The grant is pinned to what this screen showed: the save sends
 * back the hash of the declaration it displayed, and the server refuses it if
 * the app changed in the meantime. If an app later asks for more, nothing new
 * is reached until the user allows it again — the list says when that happened.
 *
 * Grant changes are immediate (the agent plane reads them on every call), unlike
 * revoking the key itself, which can take a few minutes to reach every server.
 */
import { useId, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AppWindow, Pencil, X } from 'lucide-react';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import type {
  AgentAppGrantEntry,
  AgentAppSummary,
  AgentDataAccess,
  AgentKeyAppGrant,
} from '@/lib/agent-app-grants';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

const ACCESS_LABEL: Record<AgentDataAccess, string> = {
  read: 'Read',
  read_write: 'Read and change',
};

function formatDate(value: string | null): string {
  if (!value) return 'never';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? 'unknown' : d.toLocaleDateString();
}

/** Per-collection choice in the editor: not allowed, read, or read and change. */
type Choice = Record<string, AgentDataAccess | null>;

/**
 * The editor's starting point: what was granted, but never more than the app
 * allows NOW or the key may do. A grant of `read_write` on a collection the app
 * has since narrowed to `read` starts as `read` — otherwise the (hidden) access
 * picker would keep `read_write` and every save would be refused.
 */
export function initialChoice(
  app: AgentAppSummary,
  granted: readonly AgentAppGrantEntry[] | undefined,
  canWrite: boolean,
): Choice {
  const out: Choice = {};
  for (const c of app.collections) {
    const g = granted?.find((e) => e.collection === c.name);
    out[c.name] = !g
      ? null
      : g.access === 'read_write' && c.access === 'read_write' && canWrite
        ? 'read_write'
        : 'read';
  }
  return out;
}

/** The collections to send: only ones the app shares now, never a stale choice. */
function chosenCollections(app: AgentAppSummary, choice: Choice): AgentAppGrantEntry[] {
  return app.collections.flatMap((c) => {
    const access = choice[c.name];
    return access ? [{ collection: c.name, access }] : [];
  });
}

interface ChoicesProps {
  app: AgentAppSummary;
  keyName: string;
  canWrite: boolean;
  /** What the key already holds, when changing an existing grant. */
  granted?: readonly AgentAppGrantEntry[];
  editing: boolean;
  saving: boolean;
  onSave: (app: AgentAppSummary, collections: AgentAppGrantEntry[]) => void;
  onCancel: () => void;
}

/**
 * The per-collection checkboxes for ONE version of one app's declaration. The
 * parent keys it on `${archeId}:${disclosureHash}`, so when the declaration
 * changes (a refusal refetches it) the choices start over from what the app
 * shares now — a removed collection can never be sent, and the hash sent is
 * always the one on screen.
 */
function CollectionChoices({
  app,
  keyName,
  canWrite,
  granted,
  editing,
  saving,
  onSave,
  onCancel,
}: ChoicesProps) {
  const baseId = useId();
  const [choice, setChoice] = useState<Choice>(() => initialChoice(app, granted, canWrite));
  const collections = chosenCollections(app, choice);

  return (
    <>
      <p className="text-xs text-muted-foreground">{app.description}</p>

      <fieldset className="space-y-2">
        <legend className="text-sm font-medium">What “{keyName}” may use</legend>
        {app.collections.map((c, i) => {
          const id = `${baseId}-collection-${i}`;
          const value = choice[c.name] ?? null;
          const changeAllowed = c.access === 'read_write' && canWrite;
          return (
            <div key={c.name} className="flex items-start gap-2">
              <Checkbox
                id={id}
                checked={value !== null}
                onCheckedChange={(checked) =>
                  setChoice((prev) => ({ ...prev, [c.name]: checked === true ? 'read' : null }))
                }
              />
              <div className="min-w-0 flex-1 space-y-1">
                <Label htmlFor={id} className="text-sm font-normal leading-snug">
                  {c.name}
                  <span className="block text-muted-foreground">{c.description}</span>
                  {c.hasImages && (
                    <span className="block text-muted-foreground">
                      Returns pictures the app made (previews).
                    </span>
                  )}
                </Label>
                {value !== null && changeAllowed && (
                  <select
                    aria-label={`Access to ${c.name}`}
                    className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                    value={value}
                    onChange={(e) =>
                      setChoice((prev) => ({
                        ...prev,
                        [c.name]: e.target.value as AgentDataAccess,
                      }))
                    }
                  >
                    <option value="read">{ACCESS_LABEL.read}</option>
                    <option value="read_write">{ACCESS_LABEL.read_write}</option>
                  </select>
                )}
                {value !== null && c.access === 'read_write' && !canWrite && (
                  <p className="text-xs text-muted-foreground">
                    Read only: this key was created without permission to make changes.
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </fieldset>

      <p className="text-xs text-muted-foreground">
        An agent you connect can pass what it reads here to its other tools. Allow only the apps you
        would trust it with.
      </p>

      <div className="flex gap-2">
        <Button
          type="button"
          size="sm"
          disabled={collections.length === 0 || saving}
          onClick={() => onSave(app, collections)}
        >
          {saving ? 'Saving…' : editing ? 'Save' : 'Allow'}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </>
  );
}

interface EditorProps {
  keyName: string;
  canWrite: boolean;
  /** Apps offered in the picker (already-allowed apps excluded). */
  apps: AgentAppSummary[];
  /** Present when changing an existing grant — always the CURRENT one from the query. */
  editing?: AgentKeyAppGrant & { app: AgentAppSummary };
  saving: boolean;
  onSave: (app: AgentAppSummary, collections: AgentAppGrantEntry[]) => void;
  onCancel: () => void;
}

function GrantEditor({ keyName, canWrite, apps, editing, saving, onSave, onCancel }: EditorProps) {
  const pickerId = useId();
  const [pickedId, setPickedId] = useState<string>(apps[0]?.archeId ?? '');
  // Derived every render: after a refetch the picked app may have changed (or
  // gone), and the choices below must follow what is on the server now.
  const app = editing?.app ?? apps.find((a) => a.archeId === pickedId) ?? apps[0] ?? null;

  if (!app) {
    return (
      <div className="space-y-2 rounded-md border border-dashed p-3 text-sm">
        <p className="text-muted-foreground">
          No apps to allow yet. An app appears here once it shares data with agents and you have
          used it.
        </p>
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          Close
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-md border p-3">
      {editing ? (
        <p className="text-sm font-medium">{app.name}</p>
      ) : (
        <div className="space-y-1">
          <Label htmlFor={pickerId}>App</Label>
          <select
            id={pickerId}
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
            value={app.archeId}
            onChange={(e) => setPickedId(e.target.value)}
          >
            {apps.map((a) => (
              <option key={a.archeId} value={a.archeId}>
                {a.name}
              </option>
            ))}
          </select>
        </div>
      )}
      <CollectionChoices
        key={`${app.archeId}:${app.disclosureHash}`}
        app={app}
        keyName={keyName}
        canWrite={canWrite}
        granted={editing?.granted}
        editing={editing !== undefined}
        saving={saving}
        onSave={onSave}
        onCancel={onCancel}
      />
    </div>
  );
}

/** A query that failed: say so, and offer to try again — never an empty list. */
function LoadError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex items-center justify-between gap-2 text-xs" role="alert">
      <p className="text-destructive">{message}</p>
      <Button type="button" variant="ghost" size="sm" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
}

export function AgentKeyAppAccess({ keyId, keyName }: { keyId: string; keyName: string }) {
  const queryClient = useQueryClient();
  // Only WHICH grant is being edited lives in state; the grant itself is read
  // from the query on every render, so a refetch (after a refusal, say) shows
  // the editor the declaration as it is now. `null` archeId = a new app.
  const [editor, setEditor] = useState<{ archeId: string | null } | null>(null);
  const [error, setError] = useState('');

  const grantsQuery = useQuery({
    queryKey: queryKeys.apiKeys.appGrants(keyId),
    queryFn: () => api.listAgentKeyAppGrants(keyId),
  });
  const appsQuery = useQuery({
    queryKey: queryKeys.apiKeys.agentApps(),
    queryFn: () => api.listAgentApps(),
    enabled: editor !== null && editor.archeId === null,
  });

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys.appGrants(keyId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys.agentApps() }),
    ]);

  const saveMutation = useMutation({
    mutationFn: ({
      app,
      collections,
    }: {
      app: AgentAppSummary;
      collections: AgentAppGrantEntry[];
    }) =>
      api.setAgentKeyAppGrant(keyId, app.archeId, {
        collections,
        disclosureHash: app.disclosureHash,
      }),
    onSuccess: async () => {
      setEditor(null);
      setError('');
      await refresh();
    },
    onError: async (err: unknown) => {
      setError(err instanceof Error ? err.message : 'Could not save');
      // A changed declaration is the likely cause of a refusal: fetch the new
      // one. The editor re-keys on its hash and starts over from it.
      await refresh();
    },
  });

  const removeMutation = useMutation({
    mutationFn: (archeId: string) => api.revokeAgentKeyAppGrant(keyId, archeId),
    onSuccess: async () => {
      setError('');
      await refresh();
    },
    onError: (err: unknown) => setError(err instanceof Error ? err.message : 'Could not remove'),
  });

  if (grantsQuery.isError) {
    return (
      <div className="space-y-2 border-t pt-2">
        <LoadError
          message="Could not load the apps this key can use."
          onRetry={() => void grantsQuery.refetch()}
        />
      </div>
    );
  }
  const data = grantsQuery.data;
  if (!data || !data.key.usable) return null;
  const grants = data.grants;
  const grantedIds = new Set(grants.map((g) => g.archeId));
  const offered = (appsQuery.data?.apps ?? []).filter((a) => !grantedIds.has(a.archeId));
  const editing =
    editor && editor.archeId !== null
      ? grants.find((g) => g.archeId === editor.archeId)
      : undefined;
  const editingWithApp = editing?.app ? { ...editing, app: editing.app } : undefined;

  let editorView: ReactNode = null;
  if (editor && editor.archeId !== null) {
    // The grant (or its app) can vanish under the editor — removed in another
    // tab, or the app stopped sharing. Then there is nothing left to edit.
    editorView = editingWithApp ? (
      <GrantEditor
        keyName={keyName}
        canWrite={data.key.canWrite}
        apps={[]}
        editing={editingWithApp}
        saving={saveMutation.isPending}
        onSave={(app, collections) => saveMutation.mutate({ app, collections })}
        onCancel={() => setEditor(null)}
      />
    ) : null;
  } else if (editor) {
    editorView = appsQuery.isError ? (
      <LoadError
        message="Could not load the apps you can allow."
        onRetry={() => void appsQuery.refetch()}
      />
    ) : appsQuery.isLoading ? (
      <p className="text-xs text-muted-foreground">Loading apps…</p>
    ) : (
      <GrantEditor
        keyName={keyName}
        canWrite={data.key.canWrite}
        apps={offered}
        saving={saveMutation.isPending}
        onSave={(app, collections) => saveMutation.mutate({ app, collections })}
        onCancel={() => setEditor(null)}
      />
    );
  }

  return (
    <div className="space-y-2 border-t pt-2">
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-1 text-xs font-medium">
          <AppWindow className="h-3.5 w-3.5" />
          Apps this key can use
        </p>
        {!editor && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setEditor({ archeId: null })}
          >
            Allow an app
          </Button>
        )}
      </div>

      {error && (
        <p className="text-xs text-destructive" role="alert">
          {error}
        </p>
      )}

      {grants.length === 0 && !editor && (
        <p className="text-xs text-muted-foreground">
          None. Apps that share data with agents can be allowed here, one at a time.
        </p>
      )}

      {grants.map((g) => (
        <div key={g.archeId} className="flex items-start justify-between gap-2 text-xs">
          <div className="min-w-0 space-y-0.5">
            <p className="font-medium">{g.app?.name ?? 'An app that no longer shares data'}</p>
            <p className="text-muted-foreground">
              {g.granted
                .map((e) => `${e.collection}: ${ACCESS_LABEL[e.access].toLowerCase()}`)
                .join(' · ')}
            </p>
            {g.changedSinceGranted && (
              <p className="text-muted-foreground">
                This app changed what it shares since you allowed it. Nothing new is reachable until
                you review it.
              </p>
            )}
            <p className="text-muted-foreground">Last used {formatDate(g.lastUsedAt)}</p>
          </div>
          <div className="flex shrink-0 gap-1">
            {g.app && !editor && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setEditor({ archeId: g.archeId })}
              >
                <Pencil className="h-3.5 w-3.5" />
                <span className="sr-only">Change {g.app.name}</span>
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={removeMutation.isPending}
              onClick={() => removeMutation.mutate(g.archeId)}
            >
              <X className="h-3.5 w-3.5" />
              <span className="sr-only">Remove {g.app?.name ?? g.archeId}</span>
            </Button>
          </div>
        </div>
      ))}

      {editorView}
    </div>
  );
}
